import { debounce, isEqual } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import { CAMPAIGN_PHASE_TOKEN_VALIDITY_HOURS } from 'utils/models/Campaign';
import { ONE_HOUR_IN_MILLISECONDS } from '../../consts/consts';
import {
  Campaign,
  Me,
  Organization,
  OrganizationSettings,
  Person,
  ReduxState,
} from '../../types';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import { useConfirmApi } from '../api/ApiHooks';

// we define all org settings here for use throughout the product
// so there is one place to look to get the list of settings
// NOTE: when adding something here, you'll need to add it to get_all_settings_keys
// in the backend
// @DEPRECATED: use the settings in ReduxContext instead
export const ORG_SETTING_TRAJECTORY_ADMINS = 'trajectory_admins';
export const ORG_SETTING_OBJECTIVES_RESTRICT_VISIBILITY_TO_MATCH_EDITABILITY =
  'objectives_restrict_visibility_to_match_editability';
export const OBJECTIVES_TIMEFRAME = 'objectives_timeframe';
export const OBJECTIVES_TIMEFRAME_DEFAULT_START_DATE =
  'objectives_timeframe_default_start_date';

let uniqueId = 0;
const getUniqueId = () => uniqueId++;

export function useComponentId(): number {
  const idRef = useRef(getUniqueId());
  return idRef.current;
}

export type PhaseCompletions = { [key: string]: boolean };
export type PhasePermissions = { [key: string]: boolean };

export const useCurrentActiveCampaign = (
  organization_id: number | null,
  proxyPerson: Person | null,
  userSub: string | undefined
): [
  Campaign | undefined,
  PhasePermissions,
  PhaseCompletions,
  boolean | undefined,
] => {
  const [currentActiveCampaign, setCurrentActiveCampaign] = useState<
    Campaign | undefined
  >(undefined);
  const [phasePermissions, setPhasePermissions] = useState<PhasePermissions>(
    {}
  );
  const [phaseCompletions, setPhaseCompletions] = useState<PhaseCompletions>(
    {}
  );
  const [isParticipating, setIsParticipating] = useState<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    if (organization_id && !currentActiveCampaign) {
      ConfirmAPI.getUrlWithCache(
        '/campaigns/current-active',
        'campaigns/current-active',
        userSub, // TODO: we shouldn't cache but it filckes if we do.
        proxyPerson,
        { organization: organization_id },
        (data) => {
          setCurrentActiveCampaign(data?.campaign);
          setPhasePermissions(data?.phase_permissions || {});
          setPhaseCompletions(data?.completions || {});
          setIsParticipating(data?.is_participating);
        },
        (message) => {
          // error to console but don't show to user
          console.error('Header alert error: ' + JSON.stringify(message));
        }
      );
    }
  }, [currentActiveCampaign, organization_id, proxyPerson, userSub]);

  return [
    currentActiveCampaign,
    phasePermissions,
    phaseCompletions,
    isParticipating,
  ];
};

export interface SerializableEvent {
  key: string;
  modified_at: Date;
  retryAttempt: number | undefined;
}

export type EventsSerializerSuccessCallback = (item: SerializableEvent) => void;

export interface FailedSave {
  item: SerializableEvent;
  message: any;
}

export type EventsSerializerErrorCallback = (
  message: any,
  item: SerializableEvent
) => void;

export type EventsSerializerSubmitCallback = (
  item: SerializableEvent,
  onSuccess: EventsSerializerSuccessCallback,
  onError: (message: any, error?: any) => void
) => void;

export type EventSerializerBufferFunctionType = (
  newItemOrItems: unknown | unknown[],
  preserveModifidedDate?: boolean
) => void;

export type EventSerializerContextType = [
  EventSerializerBufferFunctionType,
  boolean,
  FailedSave[],
  () => void,
];

// Hook that allow to submit versioned events that have a property 'key'.
// Without maxRetryAttempts / isRetryable, every failure is retried forever.
export const useEventsSerializer = (
  onSubmit: EventsSerializerSubmitCallback,
  onSuccessCallback: EventsSerializerSuccessCallback,
  onErrorCallback: EventsSerializerErrorCallback,
  {
    initialRetryDelayInMillis = 500,
    maxRetryAttempts,
    isRetryable,
  }: {
    initialRetryDelayInMillis?: number;
    maxRetryAttempts?: number;
    isRetryable?: (error: any) => boolean;
  } = {}
): EventSerializerContextType => {
  // Queue of events to be submitted
  const [bufferQueue, setBufferQueue] = useState<SerializableEvent[]>([]);
  // Queue of inflight events
  const [submitQueue, setSubmitQueue] = useState<SerializableEvent[]>([]);
  // Events that stopped retrying; they wait for a newer edit or a manual retry
  const [failedSaves, setFailedSaves] = useState<FailedSave[]>([]);

  const hasPendingChanges =
    bufferQueue.length > 0 || submitQueue.length > 0 || failedSaves.length > 0;

  // Dispatcher method:
  // It's only possible to pile up events in the buffer queue
  // This will take care of popping them into the submit queue and execute the submission onSubmit
  // In case of error, the event will be re-added to the buffer queue so it can be retried
  useEffect(() => {
    // If there's already an item with the same key in the submit queue, we don't want to move it
    const eligibleToSubmit = bufferQueue.filter(
      (bufferedItem) =>
        !submitQueue.find(
          (submittedItem) => submittedItem.key === bufferedItem.key
        )
    );

    // if nothing is eligible, we don't need to do anything
    if (!eligibleToSubmit.length) {
      return;
    }

    // remove the eligible items from the buffer queue
    setBufferQueue((bufferQueue) =>
      bufferQueue.filter(
        (item) =>
          !eligibleToSubmit.find(
            (itemEligible) =>
              itemEligible.key === item.key &&
              itemEligible.modified_at === item.modified_at
          )
      )
    );

    // add the eligible items to the submit queue
    setSubmitQueue((submitQueue) => [...submitQueue, ...eligibleToSubmit]);

    // submit the eligible items
    eligibleToSubmit.forEach((eligibleItem) => {
      const onSuccess = () => {
        // on success, remove the item from the submit queue...
        setSubmitQueue((submitQueue) =>
          submitQueue.filter(
            (submittedItem) => submittedItem.key !== eligibleItem.key
          )
        );
        // ...and clear its failure, unless the failure is for a newer edit
        setFailedSaves((failedSaves) =>
          failedSaves.filter(
            ({ item }) =>
              item.key !== eligibleItem.key ||
              item.modified_at > eligibleItem.modified_at
          )
        );
        onSuccessCallback(eligibleItem);
      };

      const onError = (message, error?) => {
        // increase the retry attempt counter...
        const retryAttempt =
          eligibleItem?.retryAttempt != null
            ? eligibleItem.retryAttempt + 1
            : 0;
        const eligibleItemWithRetry = {
          ...eligibleItem,
          retryAttempt,
        };
        if (
          isRetryable?.(error) === false ||
          (maxRetryAttempts != null && retryAttempt >= maxRetryAttempts)
        ) {
          setSubmitQueue((submitQueue) =>
            submitQueue.filter(
              (submittedItem) => submittedItem.key !== eligibleItem.key
            )
          );
          setFailedSaves((failedSaves) => [
            ...failedSaves.filter(({ item }) => item.key !== eligibleItem.key),
            { item: eligibleItem, message },
          ]);
          onErrorCallback(message, eligibleItemWithRetry);
          return;
        }
        //...and add it back to the buffer queue after a delay
        setTimeout(
          () => {
            // on error, remove the item from the submit queue...
            setSubmitQueue((submitQueue) =>
              submitQueue.filter(
                (submittedItem) => submittedItem.key !== eligibleItem.key
              )
            );
            addToBufferQueue(eligibleItemWithRetry, true);
          },
          initialRetryDelayInMillis * retryAttempt * 2
        );

        onErrorCallback(message, eligibleItemWithRetry);
      };

      onSubmit(eligibleItem, onSuccess, onError);
    });
  }, [
    bufferQueue,
    initialRetryDelayInMillis,
    isRetryable,
    maxRetryAttempts,
    onErrorCallback,
    onSubmit,
    onSuccessCallback,
    setBufferQueue,
    setSubmitQueue,
    submitQueue,
  ]);

  const addToBufferQueue = (newItemOrItems, preserveModifidedDate = false) => {
    const newItems = Array.isArray(newItemOrItems)
      ? newItemOrItems
      : [newItemOrItems];

    const newItemsWithModifiedDate = preserveModifidedDate
      ? newItems
      : newItems.map((item) => ({
          ...item,
          modified_at: new Date(),
        }));

    newItemsWithModifiedDate.forEach((newItem) => {
      // add the item to the buffer queue only if the version we are adding is newer than the one already in the queue
      setBufferQueue((bufferQueue) =>
        !bufferQueue.find(
          (bufferItem) =>
            bufferItem.key === newItem.key &&
            bufferItem.modified_at > newItem.modified_at
        )
          ? [...bufferQueue.filter((item) => item.key !== newItem.key), newItem] // substitute the old item with the same key in the queue with the new one
          : bufferQueue
      );
    });
  };

  // Re-queues every failed event as it was, skipping any that a newer queued or
  // in-flight edit of the same key already supersedes.
  const retryFailedSaves = () => {
    const queued = [...bufferQueue, ...submitQueue];
    setFailedSaves([]);
    addToBufferQueue(
      failedSaves
        .filter(
          ({ item }) =>
            !queued.some(
              (queuedItem) =>
                queuedItem.key === item.key &&
                queuedItem.modified_at > item.modified_at
            )
        )
        .map(({ item }) => ({ ...item, retryAttempt: undefined })),
      true
    );
  };

  return [addToBufferQueue, hasPendingChanges, failedSaves, retryFailedSaves];
};

/*
  Warns the user before they navigate away from the page when there are unsaved changes.
  - Registers a `beforeunload` handler to trigger the browser's default dialog on tab
    close/reload.
  - Returns a guard function to invoke before in-app navigation: it calls `confirmLeave`
    (which should render UI and return `true` if the user wants to leave, `false` to stay)
    and returns `true` if navigation should proceed.
*/
export const useUnsavedChangesWarning = (
  hasUnsavedChanges: boolean,
  confirmLeave: () => boolean
): (() => boolean) => {
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  return useCallback(() => {
    if (!hasUnsavedChanges) return true;
    return confirmLeave();
  }, [hasUnsavedChanges, confirmLeave]);
};

/*
  Register an escape key event on the current page and call the callback when the escape key is pressed
  Automatically get uregistered when the component unmounts
*/
export const useEscape = (onEscape: () => void) => {
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      // TODO: replace deprecated property
      if (event.keyCode === 27) onEscape();
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onEscape]);
};

/*
  Register and call the callback when the click is visually outside the ref
  Automatically get uregistered when the component unmounts
*/
export const useOutsideClick = (
  ref: React.RefObject<HTMLElement>,
  onClickOutside: () => void
) => {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClickOutside();
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClickOutside, ref]);
};

// call a function when user wakes up a tab which has been inactive for > X seconds
export const useStaleTabDetector = (onStaleTabDetected, delay = 30) => {
  const [wentInactive, setWentInactive] = useState<Date | null>(null);

  const onVisibilityStateChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      const delta = new Date().getTime() - (wentInactive?.getTime() ?? 0);
      if (delta > 1000 * delay) {
        // tab which just became visible is stale
        onStaleTabDetected();
      }
    } else if (document.visibilityState === 'hidden') {
      setWentInactive(new Date());
    }
  }, [delay, wentInactive, onStaleTabDetected]);

  useEffect(() => {
    window.addEventListener('visibilitychange', onVisibilityStateChange);
    return () =>
      window.removeEventListener('visibilitychange', onVisibilityStateChange);
  }, [onVisibilityStateChange]);
};

// Method to memo an object that has nested depth objects (arrays, objects, etc)
export function useDeepEqualMemo(value: any) {
  const ref = useRef<any>(undefined);

  if (!isEqual(ref.current, value)) {
    ref.current = value;
  }

  return ref.current;
}

interface LocalStorageStateOptions<T> {
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
  ttlInMs?: number;
}

const LOCAL_STORAGE_CUSTOM_TYPE = 'CUSTOM';

const getFromLocalStorage = ({
  key,
  userSub,
  currentProxyPerson,
  hasTTL,
}: {
  key: string;
  userSub: string | undefined;
  currentProxyPerson: Person | null;
  hasTTL: boolean;
}): any | undefined => {
  return ConfirmAPI.getObjectFromCache(
    userSub,
    currentProxyPerson,
    LOCAL_STORAGE_CUSTOM_TYPE,
    key,
    hasTTL
  );
};

const setToLocalStorage = ({
  key,
  userSub,
  currentProxyPerson,
  ttlInMs,
  value,
}: {
  key: string;
  userSub: string | undefined;
  currentProxyPerson: Person | null;
  ttlInMs?: number;
  value: any;
}) => {
  ConfirmAPI.setObjectInCache(
    userSub,
    currentProxyPerson,
    LOCAL_STORAGE_CUSTOM_TYPE,
    key,
    value,
    ttlInMs
  );
};

// A hook to save the state and initialize it from localStorage
export const useLocalStorageState = <T>(
  key: string,
  defaultValue: T | undefined,
  {
    serialize = JSON.stringify,
    deserialize = JSON.parse,
    ttlInMs = undefined,
  }: LocalStorageStateOptions<T> = {}
): [T | undefined, React.Dispatch<React.SetStateAction<T>>] => {
  const { user } = useAuthProvider();
  const userSub = user?.sub;
  const currentProxyPerson = useSelector<ReduxState, Person | null>(
    (state) => state?.currentProxyPerson
  );
  const cacheKeyParams = useMemo(
    () => ({
      key,
      userSub,
      currentProxyPerson,
      hasTTL: !!ttlInMs,
      ttlInMs,
    }),
    [key, userSub, currentProxyPerson, ttlInMs]
  );

  const [state, setState] = useState<T>(() => {
    const valueInLocalStorage = getFromLocalStorage(cacheKeyParams);

    if (valueInLocalStorage) {
      try {
        return deserialize(valueInLocalStorage);
      } catch (error) {
        ConfirmAPI.removeObjectFromCache(
          userSub,
          currentProxyPerson,
          LOCAL_STORAGE_CUSTOM_TYPE,
          key
        );
      }
    }
    return defaultValue instanceof Function ? defaultValue() : defaultValue;
  });

  const prevKeyRef = useRef(key);

  useEffect(() => {
    const prevKey = prevKeyRef.current;
    if (prevKey !== key) {
      window.localStorage.removeItem(prevKey);
    }
    prevKeyRef.current = key;

    const valueInLocalStorage = getFromLocalStorage(cacheKeyParams);
    if (isEqual(valueInLocalStorage, serialize(state))) {
      return;
    }
    setToLocalStorage({ ...cacheKeyParams, value: serialize(state) });
  }, [key, state, serialize, ttlInMs, cacheKeyParams]);

  return [state, setState];
};

// A hook to save the state and initialize it from localStorage
// or a query param with the same name
export const useLocalStorageOrQueryParamState = <T>(
  key: string,
  defaultValue: T | undefined,
  convertQueryParamValue: (v: string | null) => T,
  {
    serialize = JSON.stringify,
    deserialize = JSON.parse,
    ttlInMs = undefined,
  }: LocalStorageStateOptions<T> = {}
) => {
  const [storageValue, setStorageValue] = useLocalStorageState<T>(
    key,
    defaultValue,
    { serialize, deserialize, ttlInMs }
  );

  useEffect(() => {
    const queryParam = new URLSearchParams(location.search).get(key);
    if (queryParam) {
      setStorageValue(convertQueryParamValue(queryParam));
    }
  }, [key, setStorageValue, convertQueryParamValue]);

  return storageValue;
};

/*
  State for a group of mutually exclusive menus that open on hover (e.g. the
  flyouts of the collapsed left nav): at most one is open, identified by id.
  `closeSoon` delays closing so the pointer can cross a gap (e.g. between a
  trigger and a portaled menu) without the menu disappearing; `open`,
  `toggle` and `close` cancel any pending close, as does unmounting.
*/
export const useHoverMenu = (closeDelayInMillis: number = 150) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const open = useCallback(
    (id: string) => {
      cancelPendingClose();
      setOpenId(id);
    },
    [cancelPendingClose]
  );

  const close = useCallback(() => {
    cancelPendingClose();
    setOpenId(null);
  }, [cancelPendingClose]);

  const closeSoon = useCallback(() => {
    cancelPendingClose();
    closeTimer.current = setTimeout(() => setOpenId(null), closeDelayInMillis);
  }, [cancelPendingClose, closeDelayInMillis]);

  const toggle = useCallback(
    (id: string) => {
      cancelPendingClose();
      setOpenId((prev) => (prev === id ? null : id));
    },
    [cancelPendingClose]
  );

  useEffect(() => cancelPendingClose, [cancelPendingClose]);

  return { openId, open, close, closeSoon, toggle };
};

// A hook to get the window width and track changes
export const useWindowResize = (): number => {
  const [width, setWidth] = useState<number>(() => window.innerWidth);

  useEffect(() => {
    const handleResizeWindow = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResizeWindow);
    return () => {
      window.removeEventListener('resize', handleResizeWindow);
    };
  }, []);

  return width;
};

export const useRefDimensions = (
  myRef: React.RefObject<HTMLElement>,
  enabled: boolean | undefined = true
) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [refreshId, setRefreshId] = useState<number>(0);

  // Hack for elements that are hidden of lazily loaded
  useEffect(() => {
    // 5 Refreshes before giving up so we don't get stuck in a loop
    if (enabled && !myRef?.current && refreshId < 5) {
      setRefreshId(refreshId + 1);
    }
  }, [myRef, refreshId, enabled]);

  useEffect(() => {
    const getDimensions = () => ({
      width: (myRef && myRef?.current?.offsetWidth) || 0,
      height: (myRef && myRef?.current?.offsetHeight) || 0,
    });

    const handleResize = () => {
      setDimensions(getDimensions());
    };

    if (myRef.current) {
      setDimensions(getDimensions());
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [myRef, refreshId]);

  return dimensions;
};

export const useOrganizationSettings = (): OrganizationSettings => {
  const settings = useSelector<ReduxState, OrganizationSettings>(
    (state) => state.settings
  );
  return settings;
};

export const useCurrentOrganization = (): Organization => {
  const currentOrganization = useSelector<ReduxState, Organization>(
    (state) => state.currentOrganization
  );
  return currentOrganization;
};

export const useOrgHasLevelingFramework = (): boolean => {
  const orgHasLevelingFramework = useSelector<ReduxState, boolean>(
    (state) => state.orgHasLevelingFramework
  );
  return orgHasLevelingFramework;
};

// This hook is used to get the phase special access token
// from the query params or local storage
// and check if it's valid for the current campaign
export const useCampaignSpecialAccessToken = ({
  campaignId,
  phaseType,
}: {
  campaignId?: number;
  phaseType?: string;
}): {
  hasToken: boolean;
  hasValidToken: boolean;
  isVerifyingToken: boolean;
} => {
  const campaignSpecialAccessToken = useLocalStorageOrQueryParamState(
    'access_id',
    undefined,
    (value) => value,
    { ttlInMs: CAMPAIGN_PHASE_TOKEN_VALIDITY_HOURS * ONE_HOUR_IN_MILLISECONDS }
  );

  const { data, status } = useConfirmApi<{ status: string }>({
    method: 'POST',
    url: '/check-campaign-token',
    params: {
      campaign_token: campaignSpecialAccessToken,
      campaign_id: campaignId,
      phase_type: phaseType,
    },
    disabled: campaignSpecialAccessToken == null || !campaignId || !phaseType,
  });

  const hasToken = campaignSpecialAccessToken != null;
  const hasValidToken = data?.status === 'OK';
  const isVerifyingToken = status === 'LOADING';

  return { hasToken, hasValidToken, isVerifyingToken };
};

// This hook is used to add a delay when the setter is called
// leadingAction is called immediately, while the debounced action is called after the delay
export const useDebouncedState = <T>(
  initialValue: T | (() => T),
  leadingAction: () => void = () => {},
  delayInMillis: number = 500
): [T, Dispatch<SetStateAction<T>>] => {
  const [valueInternal, setValueInternal] = useState<T>(initialValue);
  const setValueDebounced = useRef(
    debounce(setValueInternal, delayInMillis)
  ).current;

  const setValue = useCallback(
    (newValue: SetStateAction<T>) => {
      leadingAction();
      setValueDebounced(newValue);
    },
    [setValueDebounced, leadingAction]
  );

  return [valueInternal, setValue];
};

export const useInterval = (
  callback: () => void,
  delay: number,
  enabled: boolean = true
) => {
  const savedCallback = useRef<() => void>();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback?.current?.();
    }
    if (enabled) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay, enabled]);
};

export const useTimeout = (
  callback: () => void,
  delay: number,
  enabled: boolean = true
) => {
  const savedCallback = useRef<() => void>();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the timeout.
  useEffect(() => {
    function tick() {
      savedCallback?.current?.();
    }
    if (enabled) {
      const id = setTimeout(tick, delay);
      return () => clearTimeout(id);
    }
  }, [delay, enabled]);
};

export const useMe = (): Me | undefined => {
  const me = useSelector<ReduxState, Me>((state) => state.me);
  return me;
};

/**
 * Response type from the previous campaign with person participating and feature enabled API endpoint.
 */
export interface PreviousCampaignWithPersonParticipatingAndFeatureEnabledData {
  previous_campaign_id: number | null;
}

/**
 * Options for the usePreviousCampaignWithPersonParticipatingAndFeatureEnabled hook.
 */
export interface UsePreviousCampaignWithPersonParticipatingAndFeatureEnabledOptions {
  campaignId: number | null;
  personId: number | null;
  feature: string;
  disabled?: boolean;
}

/**
 * Hook to fetch previous campaign data for a person.
 *
 * Returns the most recent previous campaign where the person participated
 * AND the specified feature was enabled.
 *
 * @param options - Configuration options
 * @returns The previous campaign data and loading state
 *
 * @example
 * ```tsx
 * const { data, status, error } = usePreviousCampaignWithPersonParticipatingAndFeatureEnabled({
 *   campaignId: campaign.id,
 *   personId: me.id,
 *   feature: 'complete_resume_step',
 * });
 *
 * if (status === 'SUCCESS' && data?.previous_campaign_id) {
 *   // Show link to previous campaign accomplishments
 * }
 * ```
 */
export const usePreviousCampaignWithPersonParticipatingAndFeatureEnabled = ({
  campaignId,
  personId,
  feature,
  disabled = false,
}: UsePreviousCampaignWithPersonParticipatingAndFeatureEnabledOptions) => {
  const { data, status, error, clear } =
    useConfirmApi<PreviousCampaignWithPersonParticipatingAndFeatureEnabledData>(
      {
        method: 'GET',
        url: `/performance/previous-campaign/${campaignId}/${personId}/${feature}`,
        disabled: disabled || !campaignId || !personId || !feature,
      }
    );

  return { data, status, error, clear };
};

/*
// A Wrapper for useState() that syncs with the URL query parameters
*/
export const useStateQueryParam = <T>(
  key: string,
  defaultValue: T,
  urlParamToValueConverter: (param: string) => T,
  valueToQueryParamConverter: (param: T) => string
): [T, (value: T) => void] => {
  const [value, setValue] = useState<T>(() => {
    const queryParam = new URLSearchParams(location.search).get(key);
    return queryParam ? urlParamToValueConverter(queryParam) : defaultValue;
  });

  useEffect(() => {
    const queryParam = new URLSearchParams(location.search).get(key);
    if (queryParam) {
      setValue(urlParamToValueConverter(queryParam));
    } else {
      setValue(defaultValue);
    }
  }, [key, defaultValue, urlParamToValueConverter]);

  const updateQueryParam = useCallback(
    (newValue?: T) => {
      const searchParams = new URLSearchParams(location.search);
      if (!newValue) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, valueToQueryParamConverter(newValue));
      }
      history.replaceState(
        {},
        '',
        `${location.pathname}?${searchParams.toString()}`
      );
      setValue(newValue ?? defaultValue);
    },
    [key, defaultValue, valueToQueryParamConverter]
  );

  return [value, updateQueryParam];
};
